import { inject, Injectable, WritableSignal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { get, sortBy } from 'lodash';
import { catchError, map, Observable, of, tap } from 'rxjs';
import { WidgetRequestService } from 'scw-digital-boards-widgets';
import { ELoadStatus } from '../../state/state.interface';
import { EndpointResponsePreviewDialogComponent } from '../components/endpoint-response-preview-dialog/endpoint-response-preview-dialog.component';
import { IDropdownOption } from '../components/mat-dropdown/mat-dropdown.component';

export interface IEndpointFetchCallbacks {
  onSuccess?: () => void;
  onFailure?: () => void;
}

/**
 * Shared logic for widget-request (endpoint) responses: fetching, extracting selectable property
 * paths, deriving example values for a selected path and previewing the raw response as JSON.
 * Used by the custom-filter and table-view-mapping add/edit dialogs.
 */
@Injectable({ providedIn: 'root' })
export class EndpointResponseService {
  private readonly widgetRequestService = inject(WidgetRequestService);
  private readonly matDialog = inject(MatDialog);
  private readonly translate = inject(TranslateService);

  /** Fetches the endpoint response of a widget request and maps the load status (and optional callbacks). */
  public fetchResponse(
    widgetRequestId: number,
    status: WritableSignal<ELoadStatus>,
    callbacks?: IEndpointFetchCallbacks,
  ): Observable<unknown> {
    return this.widgetRequestService.requestCustomWidgetRequest<unknown>({}, widgetRequestId, {}, null).pipe(
      map((response) => response.data),
      tap(() => {
        status.set(ELoadStatus.success);
        callbacks?.onSuccess?.();
      }),
      catchError(() => {
        status.set(ELoadStatus.failure);
        callbacks?.onFailure?.();

        return of(null);
      }),
    );
  }

  /** First item of a raw response — the sample used to read property paths and example values from. */
  public getSampleItem(response: unknown): unknown {
    return Array.isArray(response) ? response[0] : response;
  }

  /** Sorted dropdown options for every selectable property path in the sample item. */
  public buildPathOptions(sampleItem: unknown): IDropdownOption<string>[] {
    return sortBy(this.extractPaths(sampleItem)).map((path) => ({ id: path, name: path }));
  }

  /** Example value for the given property path, read from the sample item (empty string if none). */
  public getExampleValue(sampleItem: unknown, path: string | undefined): string {
    if (!path || !sampleItem || typeof sampleItem !== 'object') {
      return '';
    }

    const value = get(sampleItem, path.replace(/\[\]/g, '[0]'));

    if (value === undefined || value === null) {
      return '';
    }

    return typeof value === 'object' ? JSON.stringify(value) : String(value);
  }

  /** Translated "Example: <value>" hint for the selected path, or undefined when there is no example. */
  public buildExampleHint(sampleItem: unknown, path: string | undefined): string | undefined {
    const example = this.getExampleValue(sampleItem, path);

    return example ? this.translate.instant('field.exampleValue', { value: example }) : undefined;
  }

  /** Opens a dialog showing the beautified JSON of the endpoint response. */
  public openPreview(response: unknown): void {
    this.matDialog.open(EndpointResponsePreviewDialogComponent, {
      ...EndpointResponsePreviewDialogComponent.dialogConfig,
      data: {
        json: JSON.stringify(response, null, 2),
        title: this.translate.instant('field.endpointResponse'),
      },
    });
  }

  // noinspection D
  private extractPaths(data: unknown, prefix = '', paths = new Set<string>()): string[] {
    let isAddDone = false;

    if (Array.isArray(data)) {
      data.forEach((item) => {
        if (prefix) {
          isAddDone = true;
          paths.add(`${prefix}[]`);
        }

        this.extractPaths(item, `${prefix}[]`, paths);
      });
    } else if (data && typeof data === 'object') {
      for (const key of Object.keys(data)) {
        const newPath = prefix ? `${prefix}.${key}` : key;

        if (newPath && !Array.isArray(get(data, newPath))) {
          isAddDone = true;
          paths.add(newPath);
        }

        this.extractPaths(data[key as keyof typeof data], newPath, paths);
      }
    }

    if (prefix && !isAddDone) {
      paths.add(prefix);
    }

    return Array.from(paths);
  }
}
